<main class="tax-company">
  <header class="tax-company__topbar">
    <a routerLink="/dashboard/tax-accountancy" class="tax-company__brand">
      <span class="tax-company__brand-icon" aria-hidden="true"><i class="fal fa-file-invoice"></i></span>
      <span><strong>ZINT</strong><small>Tax &amp; accountancy</small></span>
    </a>
    <div class="tax-company__top-actions">
      <button type="button" class="tax-company__default-action" [disabled]="savingDefault || defaultSaved" (click)="makeDefaultView()">{{ defaultSaved ? 'Default view saved' : savingDefault ? 'Saving…' : 'Make my default view' }}</button>
      <button type="button" class="tax-company__top-action" (click)="openFullProfile()">Use full profile as default <i class="fal fa-arrow-up-right" aria-hidden="true"></i></button>
    </div>
  </header>

  <div class="tax-company__content">
    <a routerLink="/dashboard/tax-accountancy" class="tax-company__back"><i class="fal fa-arrow-left" aria-hidden="true"></i> Back to prospect search</a>

    @if (loading) {
      <div class="tax-company__state" role="status"><i class="fal fa-spinner fa-spin" aria-hidden="true"></i> Loading company profile…</div>
    } @else if (loadingError) {
      <div class="tax-company__state" role="alert">The company profile could not be loaded.</div>
    } @else {
      <section class="tax-company__hero" aria-labelledby="tax-company-name">
        <div class="tax-company__hero-main">
          <span class="tax-company__eyebrow">ACCOUNT OVERVIEW</span>
          <h1 id="tax-company-name">{{ company.company_overview?.name }}</h1>
          <div class="tax-company__meta">
            <span>Company no. {{ company.company_overview?.number || companyNumber }}</span>
            @if (company.company_overview?.company_status) {<span class="tax-company__status">{{ company.company_overview.company_status }}</span>}
            @if (company.company_overview?.latest_account_type) {<span>{{ company.company_overview.latest_account_type }}</span>}
          </div>
          @if (company.company_overview?.summary) {<p class="tax-company__summary">{{ company.company_overview.summary }}</p>}
          <div class="tax-company__links" aria-label="Company contact and social links">
            @if (websiteUrl) {<a [href]="websiteUrl" target="_blank" rel="noopener noreferrer"><i class="fal fa-globe" aria-hidden="true"></i> {{ websiteDomain }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
            @if (associatedWebsites.length) {
              <div class="tax-company__associated-websites">
                <button type="button" [attr.aria-expanded]="associatedWebsitesOpen" aria-label="Other company websites" (click)="associatedWebsitesOpen = !associatedWebsitesOpen"><i class="fal fa-globe" aria-hidden="true"></i> Other websites <i class="fal fa-chevron-down" aria-hidden="true"></i></button>
                @if (associatedWebsitesOpen) {
                  <div class="tax-company__associated-list"><strong>Other company websites</strong>
                    @for (site of associatedWebsites; track site.url) {<a [href]="site.url" target="_blank" rel="noopener noreferrer">{{ site.label }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                  </div>
                }
              </div>
            }
            @if (phoneHref) {<a [href]="phoneHref" [attr.aria-label]="'Call ' + phoneNumber"><i class="fal fa-phone" aria-hidden="true"></i> {{ phoneNumber }}</a>}
            @if (emailHref) {<a [href]="emailHref" [attr.aria-label]="'Email ' + emailAddress"><i class="fal fa-envelope" aria-hidden="true"></i> {{ emailAddress }}</a>}
            @if (linkedInUrl) {<a class="tax-company__social-link" [href]="linkedInUrl" target="_blank" rel="noopener noreferrer" [attr.aria-label]="company.company_overview?.is_direct_linkedin_url ? 'LinkedIn profile' : 'Search LinkedIn for this company'" [attr.title]="company.company_overview?.is_direct_linkedin_url ? 'LinkedIn profile' : 'Search LinkedIn for this company'"><i class="fab fa-linkedin-in" aria-hidden="true"></i></a>}
            @if (xUrl) {<a class="tax-company__social-link" [href]="xUrl" target="_blank" rel="noopener noreferrer" [attr.aria-label]="company.company_overview?.is_direct_twitter_url ? 'X profile' : 'Search X for this company'" [attr.title]="company.company_overview?.is_direct_twitter_url ? 'X profile' : 'Search X for this company'"><i class="fab fa-x-twitter" aria-hidden="true"></i></a>}
            @if (facebookUrl) {<a class="tax-company__social-link" [href]="facebookUrl" target="_blank" rel="noopener noreferrer" [attr.aria-label]="company.company_overview?.is_direct_facebook_url ? 'Facebook page' : 'Search Facebook for this company'" [attr.title]="company.company_overview?.is_direct_facebook_url ? 'Facebook page' : 'Search Facebook for this company'"><i class="fab fa-facebook-f" aria-hidden="true"></i></a>}
          </div>
        </div>
        <aside class="tax-company__hero-aside" aria-label="Accounts calendar">
          <span class="tax-company__eyebrow">ACCOUNTS CALENDAR</span>
          <div><span>Year-end reference date</span><strong>{{ company.company_overview?.financial_year_ends || 'Not recorded' }}</strong></div>
          <div><span>Latest accounts period ended</span><strong>{{ latestAccountsPeriod ? (latestAccountsPeriod | date:'d MMM y') : 'Not available' }}</strong></div>
          <div><span>Latest accounts submitted</span><strong>{{ latestAccountsSubmitted ? (latestAccountsSubmitted | date:'d MMM y') : 'Not available' }}</strong></div>
          <small>These are accounts dates, not tax filing or claim deadlines.</small>
        </aside>
      </section>

      <section class="tax-company__facts" aria-label="Company at a glance">
        <div><span>REVENUE</span><strong>{{ company.company_overview?.revenue || '—' }}</strong><small>{{ company.company_overview?.is_revenue_estimated ? 'Estimated revenue' : 'Latest available' }}</small></div>
        <div><span>HEADCOUNT</span><strong>{{ company.company_overview?.headcount?.total ?? '—' }}</strong><small>{{ company.company_overview?.headcount?.is_estimated ? 'Estimated employees' : 'Employees' }}</small></div>
        <div><span>COMPANY AGE</span><strong>{{ company.company_overview?.age || '—' }}</strong><small>Registered history</small></div>
        <div><span>PRIMARY INDUSTRY</span><strong>{{ company.company_overview?.sic_codes?.[0]?.section || '—' }}</strong><small>{{ company.company_overview?.sic_codes?.[0]?.description || 'No SIC description recorded' }}</small></div>
      </section>

      <section class="tax-company__play-section" aria-labelledby="tax-company-play-title">
        <div class="tax-company__section-heading">
          <div><span class="tax-company__eyebrow">SERVICE VIEW</span><h2 id="tax-company-play-title">Focus the account review</h2></div>
          <p>Choose the work your team is considering. The company facts stay the same.</p>
        </div>
        <div class="tax-company__play-list" role="group" aria-label="Choose a tax or accountancy service view">
          @for (play of plays; track play.id) {
            <button type="button" [class.is-active]="selectedPlay === play.id" [attr.aria-pressed]="selectedPlay === play.id" (click)="selectPlay(play.id)">
              <strong>{{ play.label }}</strong><small>{{ play.description }}</small>
            </button>
          }
        </div>
      </section>

      <section class="tax-company__evidence tax-company__panel" aria-labelledby="tax-company-evidence">
        <div class="tax-company__section-heading">
          <div><span class="tax-company__eyebrow">OPPORTUNITY REVIEW</span><h2 id="tax-company-evidence">{{ selectedPlayLabel }} evidence</h2></div>
          <p>Public and recorded signals to inspect for {{ selectedPlayDescription | lowercase }}.</p>
        </div>
        @if (visibleFacts.length) {
          <div class="tax-company__evidence-grid">
            @for (fact of visibleFacts; track $index) {
              <article class="tax-company__evidence-card">
                <span class="tax-company__evidence-label">{{ fact.label }}</span>
                <strong>{{ fact.value }}</strong>
                <div class="tax-company__evidence-source">
                  @if (fact.period) {<span>{{ periodLabel(fact.period) }}</span>}
                  @if (fact.sourceUrl) {<a [href]="fact.sourceUrl" target="_blank" rel="noopener noreferrer">{{ fact.sourceLabel }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                  @else {<span>{{ fact.sourceLabel }}</span>}
                </div>
                @if (fact.note) {<p>{{ fact.note }}</p>}
              </article>
            }
          </div>
        } @else {
          <p class="tax-company__empty">No service-specific evidence is shown from the available records. The full profile may contain more detail. Missing records do not prove that the company has no relevant activity.</p>
        }
        @if (selectedPlay === 'energy' && !hasEnergyAccess) {
          <p class="tax-company__evidence-footnote">Recorded energy figures are available only with your team's energy-data access.</p>
        } @else if (selectedPlay === 'energy' && !hasRecordedEnergy) {
          <p class="tax-company__evidence-footnote">No recorded energy figures are available in this view. This does not indicate that the company has no energy use.</p>
        }
        @if (selectedPlay === 'international_tax') {
          <p class="tax-company__evidence-footnote">This view does not confirm the immediate parent's country, connected-party transactions, transfer-pricing documentation or a tax liability. Geographic revenue and trade records may be incomplete or lack a source period.</p>
        }
        <p class="tax-company__evidence-footnote">These signals do not establish relief eligibility, claim history or an available engagement.</p>
      </section>

      <div class="tax-company__detail-grid">
        <section class="tax-company__panel" aria-labelledby="tax-company-advisers">
          <div class="tax-company__section-heading"><div><span class="tax-company__eyebrow">ACCESS &amp; ADVISERS</span><h2 id="tax-company-advisers">People and professional firms</h2></div></div>
          <div class="tax-company__adviser-grid">
            <div><span>ACCOUNTANT · RECORDED IN FILINGS</span><strong>{{ accountant || 'Not recorded' }}</strong></div>
            <div><span>AUDITOR · RECORDED IN FILINGS</span><strong>{{ auditor || 'Not recorded' }}</strong></div>
          </div>
          <p class="tax-company__subnote">The recorded firms may differ from the current advisers. Check the filing before making an introduction.</p>
          @if (people.length) {
            <div class="tax-company__people">
              @for (person of (showAllPeople ? people : people.slice(0, 6)); track $index) {
                <article class="tax-company__person">
                  <span class="tax-company__person-icon" aria-hidden="true"><i class="fal fa-user"></i></span>
                  <div class="tax-company__person-details">
                    <strong>{{ person.name }}
                      @for (code of person.nationalityCodes; track code) {
                        <small class="flag-icon tax-company__person-flag" [ngClass]="'flag-icon-' + code" aria-hidden="true"></small>
                      }
                    </strong>
                    @if (person.roles.length) {
                      <div class="tax-company__person-roles">
                        @for (role of person.roles; track role) {<span>{{ role }}</span>}
                      </div>
                    }
                    @if (person.nationalities || person.age !== undefined || person.appointedOn) {
                      <div class="tax-company__person-facts">
                        @if (person.nationalities) {<span>Nationality: {{ person.nationalities }}</span>}
                        @if (person.age !== undefined) {<span>Age: {{ person.age }}</span>}
                        @if (person.appointedOn) {<span>Appointed {{ person.appointedOn }}</span>}
                      </div>
                    }
                  </div>
                  @if (person.linkedInUrl || person.informationUrl) {
                    <div class="tax-company__person-links">
                      @if (person.linkedInUrl) {
                        <a [href]="person.linkedInUrl" target="_blank" rel="noopener noreferrer"
                          [attr.aria-label]="(person.linkedInIsDirect ? 'View LinkedIn profile for ' : 'Search LinkedIn for ') + person.name"
                          [attr.title]="person.linkedInIsDirect ? 'View LinkedIn profile' : 'Search LinkedIn'">
                          <i class="fab fa-linkedin-in" aria-hidden="true"></i>
                          @if (!person.linkedInIsDirect) {<i class="fal fa-magnifying-glass tax-company__person-link-search" aria-hidden="true"></i>}
                        </a>
                      }
                      @if (person.informationUrl) {<a [href]="person.informationUrl" target="_blank" rel="noopener noreferrer" [attr.aria-label]="'More about ' + person.name"><i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    </div>
                  }
                </article>
              }
            </div>
            @if (people.length > 6) {
              <button class="tax-company__people-toggle" type="button" (click)="showAllPeople = !showAllPeople">
                {{ showAllPeople ? 'Show fewer people' : 'Show all ' + people.length + ' people' }}
                <i class="fal" [class.fa-chevron-down]="!showAllPeople" [class.fa-chevron-up]="showAllPeople" aria-hidden="true"></i>
              </button>
            }
          } @else {<p class="tax-company__empty">No decision makers are recorded in this view.</p>}
        </section>

        <section class="tax-company__panel" aria-labelledby="tax-company-context">
          <div class="tax-company__section-heading"><div><span class="tax-company__eyebrow">COMPANY CONTEXT</span><h2 id="tax-company-context">Entity and location</h2></div></div>
          <div class="tax-company__context-row"><span>Registered address</span><strong>{{ company.company_overview?.registered_address || 'Not recorded' }}</strong></div>
          @if (company.company_overview?.trading_addresses?.length) {
            <div class="tax-company__context-row"><span>Trading locations</span><strong>{{ company.company_overview.trading_addresses.length }} recorded</strong></div>
          }
          @if (company.company_overview?.sic_codes?.length) {
            <div class="tax-company__context-row"><span>SIC classifications</span><ul>@for (sic of company.company_overview.sic_codes; track sic.code) {<li><strong>{{ sic.code }}</strong> {{ sic.description }}</li>}</ul></div>
          } @else {<div class="tax-company__context-row"><span>SIC classifications</span><strong>Not recorded</strong></div>}
          @if (company.company_overview?.is_in_group) {<p class="tax-company__subnote">This company is recorded as part of a group. Review the legal entity and ownership details in the full profile before attributing activity to it.</p>}
        </section>
      </div>

      @if (hasPAFAccess) {
        <section class="tax-company__panel tax-company__land" aria-labelledby="tax-company-land">
          <div class="tax-company__section-heading">
            <div><span class="tax-company__eyebrow">LICENSED PROPERTY DATA</span><h2 id="tax-company-land">Land ownership and EPC</h2></div>
            <p>Match the title and certificate to this legal entity before relying on the property details.</p>
          </div>
          @if (landOwnershipLoading) {
            <p class="tax-company__empty" role="status">Loading land and EPC records…</p>
          } @else if (landOwnershipData.length) {
            <div class="tax-company__land-grid">
              @for (address of landOwnershipData; track address.title_number || $index) {
                <article class="tax-company__land-card">
                  <div class="tax-company__land-heading"><strong>{{ landAddressText(address) }}</strong>@if (address.title_number) {<span>Title {{ address.title_number }}</span>}</div>
                  <div class="tax-company__land-facts">
                    @if (address.tenure) {<span>Tenure <strong>{{ address.tenure }}</strong></span>}
                    @if (address.classification) {<span>Category <strong>{{ address.classification }}</strong></span>}
                    @if (address.price_paid != null) {<span>Price paid <strong>{{ address.price_paid | currency:'GBP':'symbol':'1.0-0' }}</strong></span>}
                    @if (address.date_proprietor_added) {<span>Ownership recorded <strong>{{ address.date_proprietor_added | date:'d MMM y' }}</strong></span>}
                  </div>
                  @if (address.epc_data?.length) {
                    <div class="tax-company__epc">
                      <h3>EPC certificates</h3>
                      @for (certificate of address.epc_data; track $index) {
                        <div class="tax-company__epc-row">
                          <strong>{{ certificate.report_type || 'Certificate' }}</strong>
                          @if (certificate.property_type) {<span>{{ certificate.property_type }}</span>}
                          @if (certificate.asset_rating_band) {<span>Rating {{ certificate.asset_rating_band }}</span>}
                          @else if (certificate.asset_rating != null) {<span>Asset rating {{ certificate.asset_rating }}</span>}
                          @if (certificate.floor_area != null) {<span>{{ certificate.floor_area | number:'1.0-0' }} m²</span>}
                          @if (certificate.lodgement_date) {<small>Lodged {{ certificate.lodgement_date | date:'d MMM y' }}</small>}
                        </div>
                      }
                    </div>
                  }
                </article>
              }
            </div>
          } @else {
            <p class="tax-company__empty">No matched land title or EPC records are available for this company.</p>
          }
          <p class="tax-company__land-licence">Land ownership and EPC data is derived from Royal Mail PAF® and provided under licence. It is subject to <a href="https://drive.google.com/file/d/1OaqjlmvEiLQ1XebSl06Lg4oQyZdO-kt3/view?usp=drive_link" target="_blank" rel="noopener noreferrer">these terms</a> and must not be held out as a comprehensive address database. HM Land Registry data © Crown copyright and database right 2021, licensed under the Open Government Licence v3.0.</p>
        </section>
      }

      <section class="tax-company__panel tax-company__activity" aria-labelledby="tax-company-activity">
        <div class="tax-company__section-heading">
          <div><span class="tax-company__eyebrow">DATED ACTIVITY</span><h2 id="tax-company-activity">Sources to review</h2></div>
          <p>Recent public material can explain timing; a headline alone does not prove a tax opportunity.</p>
        </div>
        @if (hasActivity) {
          <div class="tax-company__activity-grid">
            @if (articles.length) {
              <div class="tax-company__activity-group"><h3>News and company updates</h3>
                @for (article of articles.slice(0, 4); track $index) {
                  <article><small>{{ article.source }}@if (article.date) { · {{ article.date | date:'d MMM y' }}}</small>
                    @if (article.url) {<a [href]="article.url" target="_blank" rel="noopener noreferrer">{{ article.title }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    @else {<strong>{{ article.title }}</strong>}
                  </article>
                }
              </div>
            }
            @if (visibleHiring.length) {
              <div class="tax-company__activity-group"><h3>Recent hiring</h3>
                @for (job of visibleHiring; track $index) {
                  <article><small>Job listing@if (job.date_published) { · {{ job.date_published }}}</small>
                    @if (externalUrl(job.link); as jobUrl) {<a [href]="jobUrl" target="_blank" rel="noopener noreferrer">{{ job.title || 'Role recorded' }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    @else {<strong>{{ job.title || 'Role recorded' }}</strong>}
                  </article>
                }
              </div>
            }
            @if (visibleGrants.length) {
              <div class="tax-company__activity-group"><h3>Recorded grants</h3>
                @for (grant of visibleGrants; track $index) {
                  <article><small>Grant project@if (grant.project_start_date) { · Starts {{ grant.project_start_date | date:'d MMM y' }}}</small><strong>{{ grant.project_title || 'Project recorded' }}</strong></article>
                }
              </div>
            }
            @if (visiblePatents.length) {
              <div class="tax-company__activity-group"><h3>Patent filings</h3>
                @for (patent of visiblePatents; track $index) {
                  <article><small>Patent record@if (patent.date_filed) { · Filed {{ patent.date_filed | date:'d MMM y' }}}</small>
                    @if (externalUrl(patent.ipo_link); as patentUrl) {<a [href]="patentUrl" target="_blank" rel="noopener noreferrer">{{ patent.title || 'Patent recorded' }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    @else {<strong>{{ patent.title || 'Patent recorded' }}</strong>}
                  </article>
                }
              </div>
            }
          </div>
        } @else {<p class="tax-company__empty">No dated news, hiring, grant or accessible patent records are available in this view.</p>}
      </section>

      @if (hasMagicInsightsAccess) {
        <section class="tax-company__insights" aria-labelledby="tax-company-insights">
          <div><span class="tax-company__eyebrow">MAGIC INSIGHTS</span><h2 id="tax-company-insights">Review customer-specific analysis</h2><p>Available answers, references and confidence are shown in the full profile when your team and this company have access. Opening it also makes the full profile your default view.</p></div>
          <button type="button" (click)="openFullProfile()">Review in full profile <i class="fal fa-arrow-up-right" aria-hidden="true"></i></button>
        </section>
      }
    }
  </div>
</main>
